import { QueryClient } from "@tanstack/react-query";

import { QUERY_DEFAULTS } from "@/core/constants";

import { ApiError } from "./errors";

/** Server data lives in memory only, with explicit freshness and eviction (DECISIONS.md §2.8). */
export function createQueryClient(): QueryClient {
  return new QueryClient({
    defaultOptions: {
      queries: {
        staleTime: QUERY_DEFAULTS.staleTimeMs,
        gcTime: QUERY_DEFAULTS.gcTimeMs,
        retry: (failures, error) =>
          failures < QUERY_DEFAULTS.maxRetries && error instanceof ApiError && error.retryable,
        refetchOnWindowFocus: true,
      },
      // Changes are never retried blindly: money calls carry an Idempotency-Key and are retried on purpose.
      mutations: { retry: false },
    },
  });
}

/** Query keys in one place, so a change can invalidate exactly what it affects. */
export const QUERY_KEYS = {
  me: ["me"] as const,
  overview: ["admin", "overview"] as const,
  analytics: {
    summary: (brandId: string, range: string) => ["admin", "analytics", "summary", brandId, range] as const,
    plans: (brandId: string) => ["admin", "analytics", "plans", brandId] as const,
  },
  brands: {
    all: ["admin", "brands"] as const,
    list: (filters: { search?: string; status?: string }) => ["admin", "brands", "list", filters] as const,
    detail: (brandId: string) => ["admin", "brands", "detail", brandId] as const,
    team: (brandId: string) => ["admin", "brands", "team", brandId] as const,
    commissionTerms: (brandId: string) => ["admin", "brands", "commission-terms", brandId] as const,
    payoutPolicies: (brandId: string) => ["admin", "brands", "payout-policies", brandId] as const,
  },
  settings: ["admin", "settings"] as const,
  users: {
    all: ["admin", "users"] as const,
    list: (filters: { search?: string; status?: string; role?: string }) =>
      ["admin", "users", "list", filters] as const,
    detail: (userId: string) => ["admin", "users", "detail", userId] as const,
  },
  subscriptions: {
    all: ["admin", "subscriptions"] as const,
    list: (filters: { search?: string; status?: string; brandId?: string; userId?: string }) =>
      ["admin", "subscriptions", "list", filters] as const,
    detail: (subscriptionId: string, from?: string) =>
      ["admin", "subscriptions", "detail", subscriptionId, from ?? "start"] as const,
    quote: (input: object) => ["admin", "subscriptions", "quote", input] as const,
  },
  disputes: {
    all: ["admin", "disputes"] as const,
    list: (filters: { status?: string; brandId?: string }) => ["admin", "disputes", "list", filters] as const,
  },
  payments: {
    all: ["admin", "payments"] as const,
    list: (filters: { status?: string; userId?: string; brandId?: string; subscriptionId?: string }) =>
      ["admin", "payments", "list", filters] as const,
    detail: (paymentId: string) => ["admin", "payments", "detail", paymentId] as const,
  },
  refunds: {
    all: ["admin", "refunds"] as const,
    list: (filters: { status?: string }) => ["admin", "refunds", "list", filters] as const,
  },
  payouts: {
    all: ["admin", "payouts"] as const,
    due: ["admin", "payouts", "due"] as const,
    list: (filters: { status?: string; brandId?: string }) => ["admin", "payouts", "list", filters] as const,
    detail: (payoutId: string) => ["admin", "payouts", "detail", payoutId] as const,
  },
  ledger: {
    all: ["admin", "ledger"] as const,
    list: (filters: Record<string, string | undefined>) => ["admin", "ledger", "list", filters] as const,
  },
  promoCodes: ["admin", "promo-codes"] as const,
  auditLog: (filters: Record<string, string | undefined>) => ["admin", "audit-log", filters] as const,
  dispatchTemplates: ["admin", "dispatch-templates"] as const,
  reconciliation: (month: string) => ["admin", "reconciliation", month] as const,
  reviews: {
    all: ["admin", "reviews"] as const,
    list: (filters: { status?: string; brandId?: string; rating?: number }) =>
      ["admin", "reviews", "list", filters] as const,
  },
  brandPlans: (brandId: string) => ["admin", "brands", "plans", brandId] as const,
  userAddresses: (userId: string) => ["admin", "users", "addresses", userId] as const,
};
